/**
 * ETP Related Pages Slider – Splide.js initialisation.
 *
 * Splide is enqueued by the PHP element via wp_enqueue_script('bricks-splide'),
 * so window.Splide is available on the front-end.
 *
 * Layout (max-width breakpoints, aligned with SCSS tokens: 1280=bp-m, 900=bp-s,
 * 600=bp-xs):
 *   > 1280px → 3 slides, no peek
 *   ≤ 1280px → 3 slides + right peek to viewport edge
 *   ≤ 900px  → 2 slides + right peek to viewport edge
 *   ≤ 600px  → 1 slide  + right peek to viewport edge
 *
 * Below 1280px the .etp-rps__splide element negates the container's right
 * padding via a negative margin-right, so the slider's right edge aligns
 * with the viewport edge and the peeking next card visibly extends to the
 * browser edge. See _related-pages-slider.scss for the matching SCSS.
 *
 * Arrows are pre-rendered in PHP (inside the header row) and wired manually
 * after Splide mounts. We pass `arrows: false` so Splide doesn't inject its
 * own arrow markup.
 *
 * Pagination (pill dots) is injected by Splide automatically into the .splide
 * container below the track; styling lives in _related-pages-slider.scss.
 */

export function initRelatedPagesSlider( scope ) {
  // Find all slider roots that have not been initialised yet.
  const roots = ( scope || document ).querySelectorAll(
    '.etp-rps:not([data-etp-rps-ready])'
  );

  // Splide configuration shared across all instances on the page.
  const options = {
    type:        'loop',    // Infinite loop mode.
    perPage:     3,
    perMove:     1,
    focus:       0,
    gap:         '12px',
    arrows:      false,             // Custom arrows are in the PHP header row.
    pagination:  true,              // Splide injects pill dots via SCSS styling.
    mediaQuery:  'max',             // Breakpoints below are max-width values.
    breakpoints: {
      1280: { perPage: 3, padding: { right: '36px' } },
      900:  { perPage: 2, padding: { right: '24px' } },
      600:  { perPage: 1, padding: { right: '24px' } },
    },
  };

  roots.forEach( ( root ) => {

    // Guard: Splide must be available on the window object.
    if ( ! window.Splide ) {
      console.warn( '[ETP related-pages-slider] window.Splide missing.' );
      return;
    }

    // Guard: Splide requires .splide__track and .splide__list in the DOM.
    // In Bricks builder the root can render before its children, so we skip
    // incomplete markup rather than throwing.
    const splideRoot = root.querySelector( '.splide' );
    const hasTrack   = !! root.querySelector( '.splide__track' );
    const hasList    = !! root.querySelector( '.splide__list' );

    if ( ! splideRoot || ! hasTrack || ! hasList ) {
      console.warn( '[ETP related-pages-slider] skip init, incomplete markup.' );
      return;
    }

    try {
      // Mount Splide on the inner .splide element (not the outer .etp-rps root).
      const splide = new window.Splide( splideRoot, options );
      splide.mount();

      // Wire up the custom prev/next buttons that live in .etp-rps__header.
      // splide.go('<') moves one slide back; splide.go('>') moves one forward.
      const prevBtn = root.querySelector( '.etp-rps__arrow--prev' );
      const nextBtn = root.querySelector( '.etp-rps__arrow--next' );

      if ( prevBtn ) prevBtn.addEventListener( 'click', () => splide.go( '<' ) );
      if ( nextBtn ) nextBtn.addEventListener( 'click', () => splide.go( '>' ) );

      // Mark this root as initialised so re-renders don't double-mount.
      root.dataset.etpRpsReady = 'true';

    } catch ( err ) {
      console.warn( '[ETP related-pages-slider] init failed.', err );
    }
  } );
}
